import { ref, computed } from "vue";
import api from "@/composables/api";
import { useUser } from "@/stores";

export default () => {
  const eventBuffer = ref([]);
  const isBuffering = ref(true);
  const bufferInterval = ref(null);

  // Initialize buffering on store creation
  const startBuffering = () => {
    if (bufferInterval.value) return;

    // Send events every minute
    bufferInterval.value = setInterval(() => {
      if (eventBuffer.value.length > 0) {
        sendEvents();
      }
    }, 60000); // 1 minute
  };

  const stopBuffering = () => {
    if (bufferInterval.value) {
      clearInterval(bufferInterval.value);
      bufferInterval.value = null;
    }
  };

  const addEvent = (event) => {
    const userStore = useUser();
    const user = userStore.user;

    // Add common metadata to event
    const enrichedEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      userId: user?.uid || null,
      userEmail: user?.email || null,
      sessionId: generateSessionId(), // Simple session tracking
    };

    eventBuffer.value.push(enrichedEvent);
  };

  const sendEvents = async () => {
    if (eventBuffer.value.length === 0) return;

    const events = [...eventBuffer.value];
    eventBuffer.value = []; // Clear buffer before sending to prevent duplicates

    try {
      // BE implemented POST /api/v1/analytics/events endpoint
      // Endpoint should accept an array of events in the following format:
      // {
      //   events: [{
      //     type: string,
      //     element: {
      //       // Base element properties
      //       text: string | null,      // Element's text content
      //       href: string | null,      // For links
      //       type: string | null,      // Form element type
      //       name: string | null,      // Element name attribute
      //       // Properties from data-track
      //       id: string,               // Menu item identifier
      //       type: string,             // e.g. 'main-menu', 'submenu-item'
      //       parent?: string,          // For submenu items
      //       href?: string             // Navigation target
      //     },
      //     timestamp: string,
      //     url: string,
      //     path: string,
      //     userId: string,
      //     userEmail: string,
      //     sessionId: string
      //   }]
      // }
      console.log("sending events", events);
      await api("v1/analytics/events", {
        method: "POST",
        body: { events },
      });
    } catch (error) {
      console.error("Failed to send analytics events:", error);
      // Add events back to buffer on failure
      eventBuffer.value = [...eventBuffer.value, ...events];
    }
  };

  // Generate a simple session ID - could be made more robust if needed
  const generateSessionId = () => {
    if (!window.sessionStorage.getItem("analytics_session_id")) {
      window.sessionStorage.setItem(
        "analytics_session_id",
        `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
      );
    }
    return window.sessionStorage.getItem("analytics_session_id");
  };

  // Start buffering when store is created
  startBuffering();

  // Clean up on app unmount
  window.addEventListener("unload", () => {
    stopBuffering();
    if (eventBuffer.value.length > 0) {
      // Attempt to send remaining events
      sendEvents();
    }
  });

  // State for click tracking analytics
  const users = ref([]);
  const sessions = ref([]);
  const events = ref([]);
  const usersCursor = ref(null);
  const sessionsCursor = ref(null);
  const selectedUserId = ref(null);
  const selectedSessionId = ref(null);
  const isLoadingUsers = ref(false);
  const isLoadingSessions = ref(false);
  const isLoadingEvents = ref(false);

  /**
   * Fetch users who have click tracking data
   *
   * GET /api/v1/analytics/users
   * Query params:
   * - limit: number (default: 10)
   * - cursor: string (optional, for pagination)
   *
   * Response:
   * {
   *   users: [{
   *     userId: string,
   *     userEmail: string,
   *     lastActive: string (ISO date)
   *   }],
   *   nextCursor?: string
   * }
   */
  const fetchClickUsers = async (cursor = null) => {
    try {
      isLoadingUsers.value = true;
      const response = await api("v1/analytics/users", {
        method: "GET",
        params: {
          limit: 10,
          cursor: cursor || undefined,
        },
      });

      //   console.log("fetchClickUsers response", response.data);
      //   console.log("fetchClickUsers response", response.data.users);

      if (cursor) {
        users.value.push(...response.data.users);
      } else {
        users.value = response.data.users;
      }
      usersCursor.value = response.nextCursor;
    } catch (error) {
      console.error("Failed to fetch users with click events:", error);
    } finally {
      isLoadingUsers.value = false;
    }
  };

  /**
   * Fetch sessions for a specific user
   *
   * GET /api/v1/analytics/sessions
   * Query params:
   * - userId: string (required)
   * - limit: number (default: 10)
   * - cursor: string (optional, for pagination)
   *
   * Response:
   * {
   *   sessions: [{
   *     sessionId: string,
   *     timestamp: string (ISO date),
   *     userId: string,
   *     userEmail: string
   *   }],
   *   nextCursor?: string
   * }
   */
  const fetchUserSessions = async (userId, cursor = null) => {
    try {
      isLoadingSessions.value = true;
      const response = await api("v1/analytics/sessions", {
        method: "GET",
        query: {
          userId,
          limit: 10,
          cursor: cursor || undefined,
        },
      });

      if (cursor) {
        sessions.value.push(...response.data.sessions);
      } else {
        sessions.value = response.data.sessions;
      }
      sessionsCursor.value = response.nextCursor;
    } catch (error) {
      console.error("Failed to fetch user sessions:", error);
    } finally {
      isLoadingSessions.value = false;
    }
  };

  /**
   * Fetch click events for a specific session
   *
   * GET /api/v1/analytics/events
   * Query params:
   * - sessionId: string (required)
   * - type: string (required, set to "click")
   * - limit: number (default: 10)
   * - cursor: string (optional, for pagination)
   *
   * Response:
   * {
   *   events: [{
   *     type: "click",
   *     element: {
   *       id: string,
   *       text: string | null,
   *       type: string | null,
   *       href: string | null,
   *       name: string | null
   *     },
   *     timestamp: string (ISO date),
   *     url: string,
   *     path: string,
   *     userId: string,
   *     userEmail: string,
   *     sessionId: string
   *   }],
   *   nextCursor?: string
   * }
   */
  const fetchSessionEvents = async (sessionId, cursor = null) => {
    try {
      isLoadingEvents.value = true;
      const response = await api("v1/analytics/events", {
        method: "GET",
        query: {
          sessionId,
          type: "click",
          limit: 10,
          cursor: cursor || undefined,
        },
      });

      if (cursor) {
        events.value.push(...response.data.events);
      } else {
        events.value = response.data.events;
      }
    } catch (error) {
      console.error("Failed to fetch session events:", error);
    } finally {
      isLoadingEvents.value = false;
    }
  };

  // Select a user and fetch their sessions
  const selectUser = async (userId) => {
    selectedUserId.value = userId;
    selectedSessionId.value = null;
    sessions.value = [];
    events.value = [];
    sessionsCursor.value = null;
    if (userId) {
      await fetchUserSessions(userId);
    }
  };

  // Select a session and fetch its events
  const selectSession = async (sessionId) => {
    selectedSessionId.value = sessionId;
    events.value = [];
    if (sessionId) {
      await fetchSessionEvents(sessionId);
    }
  };

  // Computed properties for loading states
  const hasMoreUsers = computed(() => Boolean(usersCursor.value));
  const hasMoreSessions = computed(() => Boolean(sessionsCursor.value));

  return {
    // Original methods and state
    eventBuffer,
    addEvent,
    startBuffering,
    stopBuffering,
    sendEvents,

    // Analytics tracking state
    users,
    sessions,
    events,
    selectedUserId,
    selectedSessionId,
    isLoadingUsers,
    isLoadingSessions,
    isLoadingEvents,
    hasMoreUsers,
    hasMoreSessions,
    fetchClickUsers,
    fetchUserSessions,
    fetchSessionEvents,
    selectUser,
    selectSession,
  };
};
